import { PageMeta } from "@/components/PageMeta";
import { PolicyLayout, type PolicySection } from "@/components/PolicyLayout";
import { siteConfig } from "@/config/site";
import { routeSeo } from "@/content/seo";

const sections: PolicySection[] = [
  { id: "purpose", label: "1. Purpose" },
  { id: "prohibited-uses", label: "2. Prohibited uses" },
  { id: "ai-and-automation", label: "3. AI and automation misuse" },
  { id: "execution-infrastructure", label: "4. Misuse of execution infrastructure" },
  { id: "third-party-platforms", label: "5. Third-party platform terms" },
  { id: "enforcement", label: "6. Enforcement" },
  { id: "reporting", label: "7. Reporting a violation" },
  { id: "contact", label: "8. Contact" },
];

export function AcceptableUsePage() {
  const seo = routeSeo["/legal/acceptable-use"];
  return (
    <>
      <PageMeta title={seo.title} description={seo.description} path="/legal/acceptable-use" />
      <PolicyLayout
        slug="acceptable-use"
        title="Acceptable Use Policy"
        intro="This policy describes prohibited uses of Devory's website and platform, and how Devory responds to violations."
        sections={sections}
      >
        <h2 id="purpose">1. Purpose</h2>
        <p>
          This policy exists to keep Devory's website, Devory Cloud, and internal pilot environment
          safe for everyone who uses them. It applies alongside the{" "}
          <a href="/legal/terms">Terms of Service</a>.
        </p>

        <h2 id="prohibited-uses">2. Prohibited uses</h2>
        <p>You may not use Devory to:</p>
        <ul>
          <li>Engage in illegal activity of any kind</li>
          <li>Develop, host, or distribute malware or other harmful code</li>
          <li>Attempt credential theft, phishing, or other deceptive attempts to obtain access</li>
          <li>Gain or attempt to gain unauthorised access to systems, data, or accounts</li>
          <li>Circumvent or attempt to circumvent security controls or usage limits</li>
          <li>
            Perform abusive scanning, probing, or denial-of-service activity against Devory or
            others
          </li>
          <li>Send spam or unsolicited bulk communications through the Service</li>
          <li>Harass, threaten, or exploit any person</li>
          <li>Infringe the intellectual property rights of Devory or any third party</li>
          <li>Engage in fraud or other deceptive commercial activity</li>
          <li>Distribute malicious extensions, plugins, or components through the Service</li>
        </ul>

        <h2 id="ai-and-automation">3. AI and automation misuse</h2>
        <p>
          You may not use Devory's AI-assisted or agent-operable functionality to bypass permission
          and confirmation boundaries, to automate the prohibited uses listed above, or to attempt
          unsupervised, autonomous actions outside the scope Devory has explicitly approved for your
          access.
        </p>

        <h2 id="execution-infrastructure">4. Misuse of execution infrastructure</h2>
        <p>
          You may not use Devory's runtime or execution infrastructure to run untrusted or malicious
          code, to attempt resource abuse (such as excessive compute, storage, or network
          consumption intended to degrade the Service for others), or to attempt to escape or
          subvert execution boundaries.
        </p>

        <h2 id="third-party-platforms">5. Third-party platform terms</h2>
        <p>
          Where your use of Devory interacts with third-party platforms or services (for example,
          through integrations or extensions), you must also comply with those third parties' own
          terms.
        </p>

        <h2 id="enforcement">6. Enforcement</h2>
        <p>
          Devory may investigate suspected violations and may suspend or terminate access, remove
          content, or take other action reasonably necessary to protect the Service and its users,
          in addition to any rights under the <a href="/legal/terms">Terms of Service</a>.
        </p>

        <h2 id="reporting">7. Reporting a violation</h2>
        <p>
          If you believe someone is violating this policy, report it through the{" "}
          <a href="/contact">contact form</a> or by emailing{" "}
          <a href={`mailto:${siteConfig.contactEmail}`}>{siteConfig.contactEmail}</a>.
        </p>

        <h2 id="contact">8. Contact</h2>
        <p>
          Questions about this policy can be sent to{" "}
          <a href={`mailto:${siteConfig.contactEmail}`}>{siteConfig.contactEmail}</a>.
        </p>
      </PolicyLayout>
    </>
  );
}
